import { motion } from "framer-motion";
import { GraduationCap, Users, Landmark } from "lucide-react";

const STATS = [
    { icon: GraduationCap, value: "10,000+", label: "Students Served" },
    { icon: Users, value: "2,000+", label: "Monthly Subscribers" },
    { icon: Landmark, value: "10 Years", label: "In Education" },
];

const AboutSection = () => {
    return (
        <section id="about" data-testid="about-section" className="relative overflow-hidden py-24 sm:py-28">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-[#7200C8]/15 blur-[150px]" />
                <div className="absolute -left-32 bottom-0 h-[360px] w-[360px] rounded-full bg-[#D9A441]/10 blur-[140px]" />
            </div>

            <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center"
                >
                    <div className="flex items-center justify-center gap-4">
                        <span className="gold-hairline h-px w-14" />
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D9A441]">Speaker - Activist - Inventor</p>
                        <span className="gold-hairline h-px w-14" />
                    </div>
                    <h2 data-testid="about-heading" className="mt-6 font-display text-3xl font-bold tracking-tight text-[#F8F5EE] sm:text-4xl lg:text-5xl">
                        About <span className="text-gold-gradient">BrothaTruth</span>
                    </h2>
                </motion.div>

                <motion.p
                    data-testid="about-lead"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="mx-auto mt-10 max-w-3xl text-center font-display text-lg leading-relaxed text-[#F4C46B]/90 sm:text-xl"
                >
                    In an era where authenticity is a rare gem, BrothaTruth emerges as a genuine voice, sharing insights and
                    experiences that resonate deeply with a diverse audience. Through a blend of personal anecdotes and
                    thought-provoking reflections, BrothaTruth invites followers into a journey of self-discovery and
                    truth-seeking, fostering a community where honesty and vulnerability are celebrated.
                </motion.p>

                <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-14">
                    <motion.p
                        data-testid="about-body-1"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="text-sm leading-loose text-[#F8F5EE]/70 sm:text-base"
                    >
                        <span className="float-left mr-3 mt-1 font-display text-5xl font-black leading-[0.8] text-[#D9A441]">C</span>
                        hief Minister Courtland Kelani-Ali (BrothaTruth), an Aniyunwiya (Sharakhi) / Choctaw American National,
                        was born and raised in the Houston, TX Territory as an aspiring musician, artist, historian, speaker and
                        writer. After attending school in Mississippi, he was intrigued by Aboriginal Studies, Heritage, Law and
                        creating businesses focused on community development &amp; outreach. Chief Minister Courtland Kelani-Ali's
                        Anglo-Saxon heritage can be traced back to Gloucestershire, England, where his family held a family seat
                        as Lords of the Manor.
                    </motion.p>

                    <motion.p
                        data-testid="about-body-2"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="text-sm leading-loose text-[#F8F5EE]/70 sm:text-base"
                    >
                        Over the years, Chief Minister Courtland Kelani-Ali would tour the world speaking his message on Law,
                        Commerce, Technology Innovation &amp; Nationality while also creating an online university titled
                        ISelfLawAmMaster.com that has now served over 10,000 students and 2,000+ monthly subscribers, teaching the
                        basics of Estate Planning, Trust Law, Patenting Inventions and Commerce. Now, with over ten years of
                        experience in the education field, Chief Minister Courtland Kelani-Ali has decided to record his
                        writings and share his knowledge with the world — to last for ages to come!
                    </motion.p>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {STATS.map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            data-testid={`about-stat-${i}`}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                            className="flex items-center gap-4 rounded-2xl border border-[#D9A441]/25 bg-white/[0.03] px-6 py-5 backdrop-blur-sm transition-colors duration-300 hover:border-[#D9A441]/50 hover:bg-white/[0.05]"
                        >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold-gradient">
                                <stat.icon className="h-5 w-5 text-[#080B12]" strokeWidth={1.8} />
                            </span>
                            <span>
                                <span className="block font-display text-xl font-bold text-[#F4C46B]">{stat.value}</span>
                                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F8F5EE]/55">{stat.label}</span>
                            </span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AboutSection;
